import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy } from "lucide-react";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const ICONS = { destra: "👉", sinistra: "👈", sorpresa: "⚖️", leale: "🤝", disonesto: "🦊", professore: "🎓" };

const winnersOf = (cat) => {
  const top = cat.ranking[0]?.votes || 0;
  return top ? cat.ranking.filter((r) => r.votes === top) : [];
};

export const hasWinners = (data) => !!data?.categories?.some((c) => c.ranking.length > 0);

export const WinnersShowcase = ({ data }) => (
  <div className="grid gap-3 sm:grid-cols-3" data-testid="awards-winners">
    {data.categories.map((cat, i) => {
      const winners = winnersOf(cat);
      return (
        <motion.div
          key={cat.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 * i, duration: 0.4 }}
          className="rounded-2xl bg-[#0b1420] border border-[#ccff00]/30 p-4 text-center"
          data-testid={`award-${cat.id}`}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-[#ccff00]">{ICONS[cat.id]} {cat.label}</p>
          {winners.length ? (
            <div className="flex flex-wrap justify-center gap-3 mt-3">
              {winners.map((w) => (
                <div key={w.key} className="flex flex-col items-center" data-testid={`award-winner-${cat.id}-${w.key.replace(/\s+/g, "-")}`}>
                  <Avatar name={w.name} photoPath={w.photo_path} size={72} className="ring-2 ring-[#ccff00]" />
                  <p className="font-heading font-bold text-slate-100 mt-2">{w.name}</p>
                  <p className="text-xs text-slate-400">{w.votes} {w.votes === 1 ? "voto" : "voti"}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-3">Nessun vincitore</p>
          )}
        </motion.div>
      );
    })}
  </div>
);

export const AwardsCeremony = ({ username }) => {
  const [data, setData] = useState(null);
  const now = new Date();
  const year = now.getFullYear() - 1;
  const seenKey = `padel_awards_seen_${year}_${(username || "").toLowerCase()}`;

  useEffect(() => {
    if (!username || now.getMonth() !== 0 || localStorage.getItem(seenKey)) return;
    api.getVotes(year).then((d) => { if (hasWinners(d)) setData(d); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const close = () => { localStorage.setItem(seenKey, "1"); setData(null); };
  if (!data) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="awards-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Trophy className="w-6 h-6 text-[#ccff00]" /> Premiazione {year}
          </DialogTitle>
          <DialogDescription className="text-slate-400">I vincitori votati da tutti gli iscritti. Complimenti!</DialogDescription>
        </DialogHeader>
        <WinnersShowcase data={data} />
        <Button onClick={close} data-testid="awards-close-btn" className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-full h-11">
          Evviva! 🎉
        </Button>
      </DialogContent>
    </Dialog>
  );
};
